import { FieldWrapper } from '@components/shared/forms';
import { UploadApiResponse as CloudinaryResponse } from 'cloudinary';
import { Notification } from '@components/shared';
import { useTranslation } from 'react-i18next';
import { CloudinaryImageParams } from '@root/types';
import AssetUploader from '@components/shared/EditableAsset/AssetUploader';
import EditableAsset from '@components/shared/EditableAsset/EditableAsset';
import useCloudinaryUploader from '@components/shared/Cloudinary/useCloudinaryUploader';
import useCloudinary from '@root/shared/useCloudinary';
import { trim } from '@cloudinary/url-gen/actions/reshape';

export type Props = {
  img: CloudinaryImageParams | null;
  onChange: (img: CloudinaryImageParams | null) => void;
  modalTitle: string;
  labelText: string;
  name: string;
  alt: string;
};

export default function ImageUploader({ img, onChange, labelText, name, alt }: Props) {
  const { t } = useTranslation('admin');
  const cld = useCloudinary();
  const {
    selectAndUploadToCloudinary,
    CloudinaryUploaderFileInput,
    cloudinaryUploaderFileInputProps,
  } = useCloudinaryUploader({
    onError: () => Notification.error({ title: t('notification.generalError') }),
  });

  return (
    <>
      <CloudinaryUploaderFileInput {...cloudinaryUploaderFileInputProps} />
      <FieldWrapper name={name} labelText={labelText} containerClass="!mb-0">
        {img ? (
          <div className="flex flex-col">
            <EditableAsset
              imagePath={
                img
                  ? cld
                      .image(img.cloudinaryId)
                      .format('auto')
                      .reshape(trim().colorSimilarity(15))
                      .toURL()
                  : ''
              }
              imageClassName="!h-[36px] rounded-[3px] !border !border-solid !border-black-020 object-contain"
              altText={alt}
              editButtonClassName="relative ml-1 top-[2px] border border-solid border-black-035 [&>svg]:!h-[16px] [&>svg]:!w-[16px] !w-4 !h-4"
              editButtonName={`${name}-edit-button`}
              onUpload={() => {
                if (selectAndUploadToCloudinary)
                  selectAndUploadToCloudinary({
                    callback: async (response: CloudinaryResponse) =>
                      onChange({
                        cloudinaryId: response.public_id,
                        transformations: { fetchFormat: 'auto', effect: 'trim:15' },
                        cropperCanvasData: null,
                      }),
                  });
              }}
              items={['upload', 'delete']}
              onDelete={() => onChange(null)}
            />
            <span className="pt-1 text-black-055 font-body-small">
              {t('microsite.topNavLogo.recommendedSize')}
            </span>
          </div>
        ) : (
          <AssetUploader
            placeholderElementClassName="pl-1 text-black-055 font-body-medium"
            containerClassName="w-[257px] !h-[52px] !my-0 bg-white"
            recommendedImageText={t('microsite.topNavLogo.recommendedSize')}
            testId={`${name}-uploader`}
            onError={() => {}}
            uploadHelpText={t('microsite.topNavLogo.uploadHelpText')}
            onReady={async (response: CloudinaryResponse) =>
              onChange({
                cloudinaryId: response.public_id,
                transformations: { fetchFormat: 'auto', effect: 'trim:15' },
                cropperCanvasData: null,
              })
            }
          />
        )}
      </FieldWrapper>
    </>
  );
}
